import React, { useEffect, useRef, useState } from 'react';
import { IonButton, IonContent, IonIcon } from '@ionic/react';
import { Prize, SpinResult } from '../../models/SpinWheel';
import './SpinWheel.css';
import { arrowForward } from 'ionicons/icons';

interface SpinWheelProps {
  prizes: Prize[];
  onSpinComplete?: (result: SpinResult) => void;
  size?: number; // Size of the wheel in pixels
  spinDuration?: number; // Duration of spin in seconds
  centerImage?: string; // Optional center image URL
}

const SpinWheel: React.FC<SpinWheelProps> = ({
  prizes,
  onSpinComplete,
  size = 300,
  spinDuration = 5,
  centerImage
}) => {
  const wheelRef = useRef<HTMLDivElement>(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [winner, setWinner] = useState<Prize | null>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchVelocity, setTouchVelocity] = useState(0);
  
  // Normalize probabilities to ensure they sum to 1
  const normalizeProbabilities = (prizes: Prize[]) => {
    const totalProbability = prizes.reduce((sum, prize) => sum + prize.probability, 0);
    
    if (totalProbability === 0) {
      // If all probabilities are 0, assign equal probabilities
      return prizes.map(prize => ({
        ...prize,
        normalizedProbability: 1 / prizes.length
      }));
    }
    
    return prizes.map(prize => ({
      ...prize,
      normalizedProbability: prize.probability / totalProbability
    }));
  };
  
  const normalizedPrizes = normalizeProbabilities(prizes);
  
  // Calculate segment angles based on probabilities
  const calculateSegmentAngles = () => {
    const segmentAngles: number[] = [];
    let startAngle = 0;
    
    normalizedPrizes.forEach(prize => {
      const angle = 360 * (prize.normalizedProbability || 1 / prizes.length);
      segmentAngles.push(startAngle + angle / 2); // Store the middle angle of each segment
      startAngle += angle;
    });
    
    return segmentAngles;
  };
  
  const segmentAngles = calculateSegmentAngles();
  
  // Select a prize based on probabilities
  const selectPrize = (): { prize: Prize, segmentIndex: number } => {
    const random = Math.random();
    let cumulativeProbability = 0;
    
    for (let i = 0; i < normalizedPrizes.length; i++) {
      cumulativeProbability += normalizedPrizes[i].normalizedProbability || 1 / prizes.length;
      if (random <= cumulativeProbability) {
        return { prize: prizes[i], segmentIndex: i };
      }
    }
    
    // Fallback to the last prize if something goes wrong
    return { prize: prizes[prizes.length - 1], segmentIndex: prizes.length - 1 };
  };
  
  const spinWheel = () => {
    if (isSpinning || prizes.length === 0) return;
    
    setIsSpinning(true);
    setWinner(null);
    
    const result = selectPrize();
    const { segmentIndex } = result;
    
    // Calculate the rotation needed to land on the selected prize
    // We add multiple full rotations (e.g., 5 * 360) and then the specific angle to land on the prize
    // We subtract from 360 because the wheel rotates clockwise but our angles increase counter-clockwise
    const targetAngle = 360 - segmentAngles[segmentIndex];
    const fullRotations = 5; // Number of full rotations before landing
    const newRotation = rotation + (fullRotations * 360) + targetAngle;
    
    setRotation(newRotation);
    
    // After the spin is complete
    setTimeout(() => {
      setIsSpinning(false);
      setWinner(result.prize);
      if (onSpinComplete) {
        onSpinComplete(result);
      }
    }, spinDuration * 1000);
  };
  
  // Handle touch/swipe to spin
  const handleTouchStart = (e: React.TouchEvent) => {
    if (isSpinning) return;
    setTouchStart(e.touches[0].clientX);
    setTouchVelocity(0);
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    if (isSpinning || touchStart === null) return;
    
    const touchMove = e.touches[0].clientX;
    const delta = touchMove - touchStart;
    
    // Update velocity based on movement
    setTouchVelocity(delta);
  };
  
  const handleTouchEnd = () => {
    if (isSpinning || touchStart === null) return;
    
    // If the swipe was fast enough, spin the wheel
    if (Math.abs(touchVelocity) > 50) {
      spinWheel();
    }
    
    setTouchStart(null);
    setTouchVelocity(0);
  };
  
  // Render wheel segments
  const renderWheelSegments = () => {
    if (prizes.length === 0) return null;
    
    const segmentSize = 360 / prizes.length;
    
    // First we render all background segments
    const backgroundSegments = prizes.map((prize, index) => {
      const rotateAngle = index * segmentSize;
      const skewCorrection = 90 - segmentSize;
      
      // Calculate colors - use provided background color or generate one
      const backgroundColor = prize.backgroundColor || 
        `hsl(${(index * 360 / prizes.length) % 360}, 70%, 60%)`;
      
      return (
        <div
          key={`segment-${prize.id}`}
          className="wheel-segment"
          style={{
            transform: `rotate(${rotateAngle}deg) skew(${skewCorrection}deg)`,
            backgroundColor,
          }}
        />
      );
    });
    
    // Then we render the content for each segment separately
    const contentElements = prizes.map((prize, index) => {
      const rotateAngle = index * segmentSize;
      const centerAngle = rotateAngle + (segmentSize / 2);
      
      // Position content at the middle of each segment
      // We use trigonometry to calculate the position
      const radius = 35; // % of the wheel radius for content placement
      const angleInRadians = (centerAngle - 90) * (Math.PI / 180);
      const x = 50 + radius * Math.cos(angleInRadians);
      const y = 50 + radius * Math.sin(angleInRadians);
      
      return (
        <div
          key={`content-${prize.id}`}
          className="prize-content"
          style={{
            position: 'absolute',
            top: `${y}%`,
            left: `${x}%`,
            transform: `translate(-50%, -50%) rotate(${centerAngle}deg)`,
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <img 
            src={prize.image} 
            alt={prize.name}
            className="prize-image"
          />
          <div className="prize-label">{prize.name}</div>
        </div>
      );
    });
    
    return (
      <>
        {backgroundSegments}
        {contentElements}
      </>
    );
  };
  
  return (
    <div className="spin-wheel-container">
      <div 
        className="wheel-outer"
        style={{ width: `${size}px`, height: `${size}px` }}
      >
        <div 
          ref={wheelRef}
          className={`wheel ${isSpinning ? 'spinning' : ''}`}
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: isSpinning ? `transform ${spinDuration}s cubic-bezier(0.1, 0.25, 0.1, 1)` : 'none',
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {renderWheelSegments()}
        </div>
        
        {/* Center of wheel */}
        <div className="wheel-center">
          {centerImage ? (
            <img src={centerImage} alt="Wheel center" className="center-image" />
          ) : (
            <div className="center-circle">POPWELL</div>
          )}
        </div>
        
        {/* Indicator/pointer */}
        <div className="wheel-pointer">
          <IonIcon icon={arrowForward} />
        </div>
      </div>
      
      {/* Spin button */}
      <IonButton 
        expand="block" 
        className="spin-button"
        onClick={spinWheel}
        disabled={isSpinning || prizes.length === 0}
      >
        {isSpinning ? 'Spinning...' : 'SPIN TO WIN!'}
      </IonButton>
      
      {/* Winner announcement */}
      {winner && !isSpinning && (
        <div className="winner-announcement">
          <h3>Congratulations!</h3>
          <p>You won: {winner.name}</p>
        </div>
      )}
    </div>
  );
};

export default SpinWheel;
